// src/context/UserContext.tsx
import React, { createContext, useContext, useState, ReactNode } from "react";

// Define the shape of your user data
export interface User {
  name?: string;
  mobile?: string;
  whatsapp?: string;
  language?: string;
  email?: string;
  country?: string;
  state?: string;
  district?: string;
  city?: string;
  pinCode?: string;
}

// Context type
interface UserContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  updateUser: (updates: Partial<User>) => void;
  clearUser: () => void;
}

// Create context
const UserContext = createContext<UserContextType | null>(null);

// Provider
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  // Update only part of user object
  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : { ...updates }));
  };

  // Reset user
  const clearUser = () => setUser(null);

  return (
    <UserContext.Provider value={{ user, setUser, updateUser, clearUser }}>
      {children}
    </UserContext.Provider>
  );
};

// Hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
